'use client'

/**
 * Sarah's launcher clips, laid over the still face. About 1.5s after the page
 * goes idle she smiles, waves and makes the call-me sign (the gesture clip),
 * then crossfades into the idle loop (smile and blink). The gesture replays at
 * most once more per session, about 45s later. The still (avatar.jpg) stays the
 * poster and the fallback: each clip sits over it at opacity 0 until its first
 * frame plays, so there is no poster fetch and no layout shift, and neither can
 * be the LCP element: nothing is fetched until the page has loaded and gone
 * idle. Off under reduced motion, Save-Data, a failed load, or
 * SARAH_AVATAR_VIDEO_ENABLED = false. Paused while the panel is open or the
 * tab is hidden.
 */
import { useEffect, useRef, useState } from 'react'
import {
  SARAH_AVATAR_GESTURE_MP4,
  SARAH_AVATAR_GESTURE_WEBM,
  SARAH_AVATAR_VIDEO_ENABLED,
  SARAH_AVATAR_VIDEO_MP4,
  SARAH_AVATAR_VIDEO_WEBM,
} from '@/lib/sarah/assets'
import {
  readSarahAvatarVideoEnv,
  readSarahGesturePlays,
  recordSarahGesturePlay,
  sarahAvatarVideoAllowed,
  sarahGestureDelay,
} from '@/lib/sarah/avatar-video'

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  cancelIdleCallback?: (id: number) => void
}

/** Run once the page has loaded and the main thread is idle. */
function afterLoadIdle(cb: () => void): () => void {
  const w = window as IdleWindow
  let idleId = 0
  let timerId = 0
  const schedule = () => {
    if (w.requestIdleCallback) idleId = w.requestIdleCallback(cb, { timeout: 4000 })
    else timerId = window.setTimeout(cb, 1500)
  }
  if (document.readyState === 'complete') schedule()
  else window.addEventListener('load', schedule, { once: true })
  return () => {
    window.removeEventListener('load', schedule)
    if (idleId && w.cancelIdleCallback) w.cancelIdleCallback(idleId)
    if (timerId) window.clearTimeout(timerId)
  }
}

/** wait: still showing, first gesture pending. gesture: gesture on top. loop: idle loop. */
type Phase = 'wait' | 'gesture' | 'loop'

export function SarahLauncherVideo({
  paused,
  enabled = SARAH_AVATAR_VIDEO_ENABLED,
}: {
  paused: boolean
  enabled?: boolean
}) {
  const [mount, setMount] = useState(false)
  const [failed, setFailed] = useState(false)
  const [phase, setPhase] = useState<Phase>('loop')
  // Render the gesture <video> only while this session still has a play left.
  const [gestureLeft, setGestureLeft] = useState(false)
  const [loopPlaying, setLoopPlaying] = useState(false)
  const [gesturePlaying, setGesturePlaying] = useState(false)
  const loopRef = useRef<HTMLVideoElement | null>(null)
  const gestureRef = useRef<HTMLVideoElement | null>(null)

  // Back to the idle loop. The gesture <video> stays only if a replay is left.
  function endGesture() {
    setGesturePlaying(false)
    setGestureLeft(sarahGestureDelay(readSarahGesturePlays()) !== null)
    setPhase('loop')
  }

  // A gesture that cannot load or play drops out for this page view; the loop stays.
  function dropGesture() {
    setGesturePlaying(false)
    setGestureLeft(false)
    setPhase('loop')
  }

  useEffect(() => {
    if (!sarahAvatarVideoAllowed({ enabled, failed: false, ...readSarahAvatarVideoEnv() })) return
    return afterLoadIdle(() => {
      const plays = readSarahGesturePlays()
      setGestureLeft(sarahGestureDelay(plays) !== null)
      setPhase(plays === 0 ? 'wait' : 'loop')
      setMount(true)
    })
  }, [enabled])

  // Schedule the next gesture. Paused (panel open) holds it; it re-arms on close.
  useEffect(() => {
    if (!mount || !gestureLeft || paused || phase === 'gesture') return
    const delay = sarahGestureDelay(readSarahGesturePlays())
    if (delay === null) return
    const id = window.setTimeout(() => {
      recordSarahGesturePlay()
      setPhase('gesture')
    }, delay)
    return () => window.clearTimeout(id)
  }, [mount, gestureLeft, paused, phase])

  useEffect(() => {
    if (!mount) return
    const loop = loopRef.current
    const gesture = gestureRef.current
    // React does not reflect `muted` as an attribute; autoplay needs it set.
    if (loop) loop.muted = true
    if (gesture) gesture.muted = true
    const sync = () => {
      const stop = paused || document.visibilityState === 'hidden'
      if (loop) {
        if (stop || phase === 'wait') loop.pause()
        else void loop.play().catch(() => setFailed(true))
      }
      if (gesture) {
        if (stop || phase !== 'gesture') gesture.pause()
        else void gesture.play().catch(dropGesture)
      }
    }
    sync()
    document.addEventListener('visibilitychange', sync)
    return () => document.removeEventListener('visibilitychange', sync)
  }, [paused, mount, phase])

  if (!mount || failed) return null

  return (
    <>
      <video
        ref={loopRef}
        className="v3-sarah__avatar-video"
        data-sarah="avatar-video"
        data-sarah-playing={loopPlaying ? '1' : '0'}
        aria-hidden="true"
        tabIndex={-1}
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        disableRemotePlayback
        onPlaying={() => setLoopPlaying(true)}
        onError={() => setFailed(true)}
      >
        <source src={SARAH_AVATAR_VIDEO_WEBM} type="video/webm" />
        <source
          src={SARAH_AVATAR_VIDEO_MP4}
          type="video/mp4"
          // The last <source> failing is the load failure; the still stays.
          onError={() => setFailed(true)}
        />
      </video>
      {gestureLeft || phase === 'gesture' ? (
        <video
          ref={gestureRef}
          className="v3-sarah__avatar-video"
          data-sarah="avatar-gesture"
          data-sarah-playing={phase === 'gesture' && gesturePlaying ? '1' : '0'}
          aria-hidden="true"
          tabIndex={-1}
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          disableRemotePlayback
          onPlaying={() => setGesturePlaying(true)}
          onEnded={endGesture}
          onError={dropGesture}
        >
          <source src={SARAH_AVATAR_GESTURE_WEBM} type="video/webm" />
          <source src={SARAH_AVATAR_GESTURE_MP4} type="video/mp4" onError={dropGesture} />
        </video>
      ) : null}
    </>
  )
}
